// The countries the deal form offers, and how a code is shown to a person.
//
// WHY THIS LIST AND NOT ALL ~250. `customer_country` is an ISO-3166-1 alpha-2
// CODE and the contract validates nothing beyond `/^[A-Z]{2}$/` — deliberately,
// because keeping the world's list in step is work with no reader. But a
// free-text box would let "Canada" through to a schema that rejects it, and
// worse, "ca" and "CAN" through to a report that groups each as its own row.
// So the form needs a picker, and a picker needs a list.
//
// This one is the markets Dial AI actually sells a utility-billing product
// into: North America first, then the English-speaking and Western European
// markets partners have brought deals from, then the APAC and LATAM entries
// worth having before someone needs them. Around thirty entries, alphabetical
// by NAME because that is the order a person scans.
//
// Two consequences, both deliberate:
//
//   * Adding a country is a one-line change here. That is cheaper than
//     maintaining the full list, and much cheaper than the alternative it
//     replaces — a form that silently accepts an unusable value.
//   * A code that is NOT in this list is still legal, and the picker PRESERVES
//     it (see `countryOptionsFor`). An agent writing `DE` through the MCP
//     server, or a code added here later and then removed, must not be
//     silently cleared by the next person who opens the form to change a city.

import type { CountryCode } from "@crm/shared";

export interface Country {
  code: CountryCode;
  name: string;
}

export const COUNTRIES: readonly Country[] = [
  { code: "AR", name: "Argentina" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "BR", name: "Brazil" },
  { code: "CA", name: "Canada" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colombia" },
  { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "IN", name: "India" },
  { code: "IE", name: "Ireland" },
  { code: "IT", name: "Italy" },
  { code: "JP", name: "Japan" },
  { code: "MX", name: "Mexico" },
  { code: "NL", name: "Netherlands" },
  { code: "NZ", name: "New Zealand" },
  { code: "NO", name: "Norway" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SG", name: "Singapore" },
  { code: "ZA", name: "South Africa" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
];

const NAME_BY_CODE: ReadonlyMap<string, string> = new Map(
  COUNTRIES.map((country) => [country.code, country.name]),
);

/**
 * "CA" -> "Canada", and an unlisted code back to itself. Never null: a code we
 * don't have a name for is still the truth about that deal, and rendering it
 * raw is better than rendering nothing.
 */
export function countryName(code: string): string {
  return NAME_BY_CODE.get(code) ?? code;
}

/** "Canada (CA)" — the code is what is stored, so the code stays on screen. */
export function countryLabel(code: string): string {
  const name = NAME_BY_CODE.get(code);
  return name === undefined ? `${code} — not in the list` : `${name} (${code})`;
}

export interface CountryOption {
  value: string;
  label: string;
}

/**
 * The picker's options, with `selected` appended when it is a legal code this
 * list does not carry. Without that, opening an existing deal placed in an
 * unlisted country would show an empty select, and saving anything else on the
 * form would look like the user had cleared the country on purpose.
 */
export function countryOptionsFor(selected: string): readonly CountryOption[] {
  const options = COUNTRIES.map((country) => ({
    value: country.code,
    label: `${country.name} (${country.code})`,
  }));
  if (selected === "" || NAME_BY_CODE.has(selected)) return options;
  return [...options, { value: selected, label: countryLabel(selected) }];
}
